<script setup>
import { nextTick, onMounted } from 'vue';
import { eventCategories, events } from '@js/utils/event';
import { EventDispatcher, getOffset, isMobileView, logEvent, qs, qsa } from '@js/utils/toolbox';

const SCROLL_OFFSET = 83;

/**
 * Returns the index of the view whose offset is closest to the calculated scroll position.
 * @param scrollTop - The current scrollTop of the container.
 * @param views - An array of view elements.
 */
const getViewIndex = (scrollTop = 0, views) => {
	const calcScrollTop = scrollTop + SCROLL_OFFSET;
	const tops = views.map(view => view.offsetTop);
	const closest = tops.reduce((prev, curr) =>
		Math.abs(curr - calcScrollTop) < Math.abs(prev - calcScrollTop) ? curr : prev
	);
	return tops.indexOf(closest);
};

/**
 * Click handler for the up and down scroll buttons.
 */
const buttonClick = (event) => {
	const scrollUpBtn = qs('.filtersScrollBtn--up');
	const scrollDownBtn = qs('.filtersScrollBtn--down');
	const parent = qs('#filters');
	const content = qs('.filtersContent');
	const scrollTop = content.scrollTop;
	const views = qsa('.filtersView');

	const target = event.target;
	const closestIndex = getViewIndex(scrollTop, views);

	let view = views[closestIndex];
	let yPos = 0;
	const viewFoldPos = view.offsetTop - SCROLL_OFFSET;
	const isAtFold = scrollTop === viewFoldPos;

	// Handle scrolling up
	if (target === scrollUpBtn) {
		if (scrollTop < viewFoldPos || isAtFold) {
			view = views[closestIndex - 1];
		}
		if (view) {
			yPos = view.offsetTop - SCROLL_OFFSET < 30 ? 0 : view.offsetTop - SCROLL_OFFSET;
		}
	}

	// Handle scrolling down
	if (target === scrollDownBtn) {
		if (scrollTop > viewFoldPos || isAtFold || closestIndex === 0) {
			view = views[closestIndex + 1];
		}
		if (view) {
			yPos = view.offsetTop - SCROLL_OFFSET;
		}
	}

	// If the last view is partially visible and parent is in view, scroll the window to the parent element
	const lastViewProximity = yPos + content.clientHeight - SCROLL_OFFSET;
	if (lastViewProximity > content.scrollHeight && parent.getBoundingClientRect().top > 0) {
		window.scrollTo({
			top: getOffset(parent).top,
			behavior: 'smooth'
		});
	}

	content.scrollTo({
		top: yPos,
		behavior: 'smooth'
	});
};

onMounted(async () => {
	await nextTick();

	const scrollUpBtn = qs('.filtersScrollBtn--up');
	const scrollDownBtn = qs('.filtersScrollBtn--down');
	const parent = qs('#filters');
	const content = qs('.filtersContent');

	/**
   * Adjusts the bottom padding of the content to ensure the scroll down button is properly positioned.
   */
	function addPaddingBottom () {
		const computedStyle = window.getComputedStyle(content);
		const currentPaddingBottom = parseFloat(computedStyle.paddingBottom);
		const contentBottom = content.offsetHeight + content.getBoundingClientRect().top + currentPaddingBottom;
		if (contentBottom > window.innerHeight) {
			const offsetTop = parent.getBoundingClientRect().top;
			content.style.paddingBottom = `${offsetTop}px`;
			scrollDownBtn.style.bottom = `${offsetTop}px`;
		} else {
			content.style.paddingBottom = '0';
		}
	}

	/**
   * Toggles the visibility of the scroll buttons based on the current scroll position.
   */
	function scrollCallback () {
		// Do not apply scroll button logic for mobile view.
		if (isMobileView()) {
			return;
		}

		const currentScrollTop = content.scrollTop;

		// Show or hide the up button
		if (currentScrollTop < 5) {
			hideScrollBtn(scrollUpBtn);
		} else {
			showScrollBtn(scrollUpBtn);
		}

		addPaddingBottom();

		// Show or hide the down button
		if (currentScrollTop === content.scrollHeight - content.offsetHeight) {
			hideScrollBtn(scrollDownBtn);
		} else {
			showScrollBtn(scrollDownBtn);
		}
	}

	// Scroll event listener for the content element
	content.addEventListener('scroll', scrollCallback);
	window.addEventListener('scroll', scrollCallback);

	/**
   * Shows the given scroll button and logs the event when necessary.
   * @param btn - The button element.
   */
	function showScrollBtn (btn) {
		if (!btn.classList.contains('filtersScrollBtn--visible')) {
			btn.classList.add('filtersScrollBtn--visible');
			// Log event for scrolling if it is the up button and the parent hasn't been marked scrolled yet.
			if (btn.classList.contains('filtersScrollBtn--up') && !qs('#filters').classList.contains('scrolled')) {
				qs('#filters').classList.add('scrolled');
				logEvent(events.scrollFilterList, eventCategories.scroll);
			}
		}
	}

	/**
   * Hides the given scroll button.
   * @param btn - The button element.
   */
	function hideScrollBtn (btn) {
		if (btn.classList.contains('filtersScrollBtn--visible')) {
			btn.classList.remove('filtersScrollBtn--visible');
			if (btn.classList.contains('filtersScrollBtn--down')) {
				btn.style.bottom = '';
			}
		}
	}

	// Run the scroll callback initially to set the appropriate button states.
	scrollCallback();

	// Add padding to the content to ensure the scroll down button is properly positioned.
	EventDispatcher.once('showresult:resultsUpdated', scrollCallback);
	EventDispatcher.addEventListener('displayDirector:showcase:close', scrollCallback);
});
</script>

<template>
  <div class="filtersScrollBtns">
    <div
      class="filtersScrollBtn filtersScrollBtn--up"
      @click="buttonClick"
    >
      <div class="filtersScrollBtn__arrows" />
    </div>
    <div
      class="filtersScrollBtn filtersScrollBtn--down"
      @click="buttonClick"
    >
      <div class="filtersScrollBtn__arrows" />
    </div>
  </div>
</template>

<style lang="scss">
.filtersScrollBtns {
  position: absolute;
  left: -999.9rem;
  top: -999.9rem;
  width: 100%;
  height: calc(100% - 5.4rem);
  pointer-events: none;

  @include breakpoint_up(md) {
    left: auto;
    top: auto;
  }
}

.filtersScrollBtn {
  z-index: 999;
  position: absolute;
  left: -999.9rem;
  top: -999.9rem;
  width: 100%;
  height: 3.8rem;
  background-color: $white;
  cursor: pointer;
  pointer-events: all;
//   transition: all 0.3s linear;
}

.filtersScrollBtn__arrows {
  position: relative;
  display: flex;
  color: $main-font-color;
  flex-direction: column;
  font-weight: 600;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: $white;
  font-family: "icomoon";
  font-size: 1.7rem;
  speak: none;
  pointer-events: none;

  & > * {
    pointer-events: none;
  }

  &::before,
  &::after {
    display: block;
    line-height: 0.4;
  }
}

.filtersScrollBtn--up {
  box-shadow: 0.1rem 0.6rem 1rem 0 rgba(46, 91, 139, 0.09);

  .filtersScrollBtn__arrows {
    &::before,
    &::after {
      content: "\e907";
    }
  }
}

.filtersScrollBtn--down {
  box-shadow: -0.1rem -0.6rem 1rem 0 rgba(46, 91, 139, 0.09);

  &::before {
    content: '';
    position: absolute;
    background-color: $white;
    width: 100%;
    left: 0;
    top: 0;
    height: 25.1rem;
  }

  .filtersScrollBtn__arrows {
    &::before,
    &::after {
      content: "\e917";
    }
  }
}

.filtersScrollBtn--visible {
  &:first-child {
    left: 0;
    top: 0;

    .filtersScrollBtn__arrows {
    //   animation-name: bounce-reverse;
    }
  }

  &:last-child {
    left: 0;
    top: auto;
    bottom: 0;
  }

  .filtersScrollBtn__arrows {
    // animation: bounce 2s linear 0s 1 normal;
  }
}
</style>
